import React, { useState, useEffect } from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExploreIcon from '@mui/icons-material/Explore';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import RouteIcon from '@mui/icons-material/Route';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import BalanceIcon from '@mui/icons-material/Balance';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

const loadingMessages = [
  { text: 'Analyzing your preferences...', icon: <PsychologyIcon sx={{ fontSize: 40 }} /> },
  { text: 'Finding the best attractions...', icon: <ExploreIcon sx={{ fontSize: 40 }} /> },
  { text: 'Crafting your perfect itinerary...', icon: <EditCalendarIcon sx={{ fontSize: 40 }} /> },
  { text: 'Optimizing travel routes...', icon: <RouteIcon sx={{ fontSize: 40 }} /> },
  { text: 'Selecting top-rated experiences...', icon: <StarIcon sx={{ fontSize: 40 }} /> },
  { text: 'Adding local hidden gems...', icon: <DiamondIcon sx={{ fontSize: 40 }} /> },
  { text: 'Balancing activities and rest time...', icon: <BalanceIcon sx={{ fontSize: 40 }} /> },
  { text: 'Almost there...', icon: <RocketLaunchIcon sx={{ fontSize: 40 }} /> },
];

interface BuildingTripLoaderProps {
  open: boolean;
}

function BuildingTripLoader({ open }: BuildingTripLoaderProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    if (!open) {
      setMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        setFadeIn(true);
      }, 200);
    }, 2000);

    return () => clearInterval(interval);
  }, [open]);

  return (
    <Backdrop
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
      }}
      open={open}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <CircularProgress color="primary" size={60} thickness={4} />
        <Fade in={fadeIn} timeout={300}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              minHeight: '5em',
            }}
          >
            {loadingMessages[messageIndex].icon}
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                px: 2,
              }}
            >
              {loadingMessages[messageIndex].text}
            </Typography>
          </Box>
        </Fade>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            mt: 1,
          }}
        >
          Creating your personalized trip plan
        </Typography>
      </Box>
    </Backdrop>
  );
}

export default BuildingTripLoader;
